import { DestroyRef, Injectable, inject, signal, type Signal } from '@angular/core';
import {
  createExplorerStore,
  type DocumentItem,
  type ExplorerSnapshot,
  type ExplorerStore,
  type ExplorerStoreConfig,
} from '@document-explorer/core';

/**
 * Bridges the framework-free core store to Angular signals.
 *
 * Everything the explorer *does* still lives in `@document-explorer/core` —
 * this class only republishes its snapshot as a signal, which is why the
 * Angular component behaves identically to the React one by construction.
 */
@Injectable()
export class ExplorerStoreService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly inner = createExplorerStore<unknown>();
  private readonly snapshotSignal = signal<ExplorerSnapshot>(this.inner.getSnapshot());

  constructor() {
    const unsubscribe = this.inner.subscribe(() => this.snapshotSignal.set(this.inner.getSnapshot()));
    this.destroyRef.onDestroy(unsubscribe);
  }

  /** The current render-ready view of the data. */
  readonly snapshot: Signal<ExplorerSnapshot> = this.snapshotSignal.asReadonly();

  get store(): ExplorerStore<unknown> {
    return this.inner;
  }

  get actions(): ExplorerStore<unknown>['actions'] {
    return this.inner.actions;
  }

  configure(patch: Pick<ExplorerStoreConfig, 'rootLabel' | 'search'>): void {
    this.inner.configure(patch);
  }

  isSelected(item: DocumentItem): boolean {
    return this.snapshotSignal().state.selection.ids.has(item.id);
  }
}
